import {
  buildResponseValidator,
  presignPayloadSchema,
  questionnaireSchema,
  responsePayloadSchema,
  type Questionnaire,
  type ResponsePayload,
} from '@juror-portal/schema';
import { seedQuestionnaire } from '../features/questionnaire-builder/utils/seedQuestionnaire';
import type { QuestionnaireSummary } from '../features/questionnaire-builder/hooks/useQuestionnaires';

// Real API responses wrap the Questionnaire in a version envelope
// ({versionId, versionNumber, schema}) - the hooks (useQuestionnaire,
// useSaveQuestionnaire, useCreateQuestionnaire) already expect this shape.
// This mock predates that contract and returned the bare object; wrap it here
// so mock mode actually exercises what the hooks assume rather than silently
// returning undefined for `.schema`.
const toEnvelope = (q: Questionnaire) => ({
  versionId: q.id,
  versionNumber: 1,
  schema: q,
});

const toSummary = (q: Questionnaire, isActive: boolean): QuestionnaireSummary => ({
  id: q.id,
  slug: q.id,
  jurisdiction: q.jurisdiction,
  term: q.term,
  title: q.title,
  isActive,
  currentVersionNumber: 1,
  updatedAt: q.updatedAt,
});

const STORAGE_PREFIX = 'juror-portal:mock-api:v1';
const QUESTIONNAIRES_KEY = `${STORAGE_PREFIX}:questionnaires`;
const RESPONSES_KEY = `${STORAGE_PREFIX}:responses`;
// Mirrors the real API's approach (EffectiveFromDate/EffectiveToDate - see
// BuilderQuestionnaireAdapter.SetActive) in spirit: there's no isActive field on Questionnaire
// itself, so the toggle state lives in its own side-store keyed by id. Absent = active (matches
// "no disabled questionnaires yet" being the default state).
const ACTIVE_FLAGS_KEY = `${STORAGE_PREFIX}:active-flags`;

interface StoredQuestionnaires {
  [id: string]: Questionnaire;
}

interface StoredActiveFlags {
  [id: string]: boolean;
}

interface StoredResponses {
  [questionnaireId: string]: Array<
    ResponsePayload & { responseId: string; submittedAt: string }
  >;
}

const readStore = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeStore = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.warn('[mockApi] localStorage write failed', err);
  }
};

const seedIfEmpty = () => {
  const existing = readStore<StoredQuestionnaires>(QUESTIONNAIRES_KEY, {});
  if (Object.keys(existing).length === 0) {
    writeStore(QUESTIONNAIRES_KEY, { [seedQuestionnaire.id]: seedQuestionnaire });
  }
};

const json = (status: number, body: unknown): Response =>
  new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });

const noContent = (): Response => new Response(null, { status: 204 });

const errorBody = (code: string, message: string) => ({ code, message });

const randomId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface RouteContext {
  pathParts: string[];
  body: unknown;
  searchParams: URLSearchParams;
}

type RouteHandler = (ctx: RouteContext) => Promise<Response> | Response;

interface RouteDefinition {
  method: string;
  // Path pattern split on '/'. Use ':' prefix to mark a parameter segment.
  pattern: string[];
  handler: RouteHandler;
}

const matchPath = (pattern: string[], parts: string[]): string[] | null => {
  if (pattern.length !== parts.length) return null;
  const captures: string[] = [];
  for (let i = 0; i < pattern.length; i++) {
    const p = pattern[i];
    if (p.startsWith(':')) {
      captures.push(parts[i]);
    } else if (p !== parts[i]) {
      return null;
    }
  }
  return captures;
};

const listQuestionnaires: RouteHandler = ({ searchParams }) => {
  const includeDisabled = searchParams.get('includeDisabled') === 'true';
  const store = readStore<StoredQuestionnaires>(QUESTIONNAIRES_KEY, {});
  const activeFlags = readStore<StoredActiveFlags>(ACTIVE_FLAGS_KEY, {});
  const items = Object.values(store)
    .map((q) => toSummary(q, activeFlags[q.id] ?? true))
    .filter((s) => includeDisabled || s.isActive);
  return json(200, { items });
};

const setActiveQuestionnaire: RouteHandler = ({ pathParts, body }) => {
  const id = pathParts[pathParts.length - 1];
  const store = readStore<StoredQuestionnaires>(QUESTIONNAIRES_KEY, {});
  if (!(id in store)) {
    return json(404, errorBody('NOT_FOUND', 'Questionnaire not found'));
  }
  const isActive = Boolean((body as { isActive?: unknown } | undefined)?.isActive);
  const activeFlags = readStore<StoredActiveFlags>(ACTIVE_FLAGS_KEY, {});
  activeFlags[id] = isActive;
  writeStore(ACTIVE_FLAGS_KEY, activeFlags);
  return noContent();
};

const getQuestionnaire: RouteHandler = ({ pathParts }) => {
  const id = pathParts[pathParts.length - 1];
  const store = readStore<StoredQuestionnaires>(QUESTIONNAIRES_KEY, {});
  const found = store[id];
  if (!found) return json(404, errorBody('NOT_FOUND', 'Questionnaire not found'));
  return json(200, toEnvelope(found));
};

const putQuestionnaire: RouteHandler = ({ pathParts, body }) => {
  const id = pathParts[pathParts.length - 1];
  const parsed = questionnaireSchema.safeParse(body);
  if (!parsed.success) {
    return json(
      400,
      errorBody(
        'INVALID_PAYLOAD',
        `Invalid questionnaire: ${parsed.error.issues
          .map((i) => `${i.path.join('.')}: ${i.message}`)
          .join('; ')}`,
      ),
    );
  }
  if (parsed.data.id !== id) {
    return json(400, errorBody('ID_MISMATCH', 'Body id does not match path id'));
  }
  const updated = { ...parsed.data, updatedAt: new Date().toISOString() };
  const store = readStore<StoredQuestionnaires>(QUESTIONNAIRES_KEY, {});
  store[id] = updated;
  writeStore(QUESTIONNAIRES_KEY, store);
  return json(200, toEnvelope(updated));
};

const deleteQuestionnaire: RouteHandler = ({ pathParts }) => {
  const id = pathParts[pathParts.length - 1];
  const store = readStore<StoredQuestionnaires>(QUESTIONNAIRES_KEY, {});
  if (!(id in store)) {
    return json(404, errorBody('NOT_FOUND', 'Questionnaire not found'));
  }
  delete store[id];
  writeStore(QUESTIONNAIRES_KEY, store);
  return noContent();
};

const submitResponse: RouteHandler = ({ body }) => {
  const payload = responsePayloadSchema.safeParse(body);
  if (!payload.success) {
    return json(
      400,
      errorBody(
        'INVALID_PAYLOAD',
        `Invalid response: ${payload.error.issues.map((i) => i.message).join('; ')}`,
      ),
    );
  }
  const store = readStore<StoredQuestionnaires>(QUESTIONNAIRES_KEY, {});
  const questionnaire = store[payload.data.questionnaireId];
  if (!questionnaire) {
    return json(
      404,
      errorBody('QUESTIONNAIRE_NOT_FOUND', 'Questionnaire not found'),
    );
  }
  const validation = buildResponseValidator(
    questionnaire,
    payload.data.answers,
  ).safeParse(payload.data);
  if (!validation.success) {
    return json(
      400,
      errorBody(
        'VALIDATION_FAILED',
        `Validation failed: ${validation.error.issues
          .map((i) => `${i.path.join('.')}: ${i.message}`)
          .join('; ')}`,
      ),
    );
  }
  const responseId = randomId();
  const submittedAt = new Date().toISOString();
  const responses = readStore<StoredResponses>(RESPONSES_KEY, {});
  const existing = responses[payload.data.questionnaireId] ?? [];
  responses[payload.data.questionnaireId] = [
    ...existing,
    { ...payload.data, responseId, submittedAt },
  ];
  writeStore(RESPONSES_KEY, responses);
  return json(200, { responseId, submittedAt });
};

const presignUpload: RouteHandler = ({ body }) => {
  const payload = presignPayloadSchema.safeParse(body);
  if (!payload.success) {
    return json(400, errorBody('INVALID_PAYLOAD', 'Invalid payload'));
  }
  // No real S3 in mock mode — just hand back a fake URL the front-end can show.
  const safeName = payload.data.fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const key = `uploads/${payload.data.questionnaireId}/${payload.data.questionId}/${randomId()}-${safeName}`;
  return json(200, {
    uploadUrl: `mock://upload/${key}`,
    key,
    expiresIn: 300,
  });
};

// Mirrors JurorApi.Shared.Questionnaire.QuestionTypeLookup's real output shape (sourced from
// Dictionary.tblQuestionnaireCustomQuestionType there) - kept in sync by hand here since the mock
// has no DB to read. inputKind 'file' is deliberately absent: no legacy type maps to it.
const listQuestionTypes: RouteHandler = () =>
  json(200, [
    { legacyTypeId: 1, legacyDescription: 'Yes/No', inputKind: 'boolean', needsOptions: false, supportsFollowUps: true, hardCoded: false },
    { legacyTypeId: 2, legacyDescription: 'Text', inputKind: 'text', needsOptions: false, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 3, legacyDescription: 'Multi-Line Text', inputKind: 'long-text', needsOptions: false, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 4, legacyDescription: 'Email', inputKind: 'email', needsOptions: false, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 5, legacyDescription: 'Phone', inputKind: 'tel', needsOptions: false, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 6, legacyDescription: 'Number', inputKind: 'numeric', needsOptions: false, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 7, legacyDescription: 'List', inputKind: 'options-single', needsOptions: true, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 8, legacyDescription: 'Date', inputKind: 'date', needsOptions: false, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 9, legacyDescription: 'Checkbox', inputKind: 'single-checkbox', needsOptions: false, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 10, legacyDescription: 'Checkbox List', inputKind: 'options-multi', needsOptions: true, supportsFollowUps: false, hardCoded: false },
    { legacyTypeId: 12, legacyDescription: 'Name Address Panel', inputKind: 'address', needsOptions: false, supportsFollowUps: false, hardCoded: true },
    { legacyTypeId: 15, legacyDescription: 'Large Numbers', inputKind: 'decimal', needsOptions: false, supportsFollowUps: false, hardCoded: false },
  ]);

const ROUTES: RouteDefinition[] = [
  { method: 'GET', pattern: ['questionnaires'], handler: listQuestionnaires },
  { method: 'GET', pattern: ['questionnaires', ':id'], handler: getQuestionnaire },
  { method: 'PUT', pattern: ['questionnaires', ':id'], handler: putQuestionnaire },
  { method: 'PATCH', pattern: ['questionnaires', ':id'], handler: setActiveQuestionnaire },
  { method: 'DELETE', pattern: ['questionnaires', ':id'], handler: deleteQuestionnaire },
  { method: 'POST', pattern: ['responses'], handler: submitResponse },
  { method: 'POST', pattern: ['uploads', 'presign'], handler: presignUpload },
  { method: 'GET', pattern: ['question-types'], handler: listQuestionTypes },
];

const matchRoute = (
  method: string,
  pathParts: string[],
): { route: RouteDefinition; pathParts: string[] } | null => {
  for (const route of ROUTES) {
    if (route.method !== method) continue;
    const captures = matchPath(route.pattern, pathParts);
    if (captures) return { route, pathParts };
  }
  return null;
};

const toMockUrl = (url: string): URL =>
  // Accept absolute and relative URLs.
  url.startsWith('http') ? new URL(url) : new URL(url, 'http://mock.local');

const readBody = async (init?: RequestInit): Promise<unknown> => {
  if (!init?.body) return undefined;
  if (typeof init.body === 'string') {
    try {
      return JSON.parse(init.body);
    } catch {
      return init.body;
    }
  }
  return init.body;
};

const SIMULATED_LATENCY_MS = 180;

let installed = false;

export const installMockApi = () => {
  // Re-seed every call so that test resets repopulate the store.
  seedIfEmpty();
  if (installed) return;
  installed = true;

  const realFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const method = (init?.method ?? 'GET').toUpperCase();
    const parsedUrl = toMockUrl(url);
    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);

    const match = matchRoute(method, pathParts);
    if (!match) return realFetch(input, init);

    await wait(SIMULATED_LATENCY_MS);
    const body = await readBody(init);
    return match.route.handler({
      pathParts: match.pathParts,
      body,
      searchParams: parsedUrl.searchParams,
    });
  };

  console.info(
    '[mockApi] installed — questionnaire CRUD + response submit are served from localStorage',
  );
};

/** Test helper — wipes the local store. */
export const __resetMockApi = () => {
  try {
    localStorage.removeItem(QUESTIONNAIRES_KEY);
    localStorage.removeItem(RESPONSES_KEY);
    localStorage.removeItem(ACTIVE_FLAGS_KEY);
  } catch {
    // ignore — localStorage unavailable in some test contexts
  }
};
